import React from 'react';
import { motion } from 'framer-motion';
import { Activity, ExternalLink, FileText } from 'lucide-react';
import { SiGithub, SiX } from '@icons-pack/react-simple-icons';
import { Cpu } from 'lucide-react';
import { formatDate, getActivityDisplayName } from '../../utils/formatters';
import PremiumButton from '../ui/PremiumButton';

// Activity History Table Component
const ActivityHistory = ({ 
  history, 
  currentPage, 
  itemsPerPage, 
  totalPages, 
  onPageChange 
}) => {
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentHistory = history.slice(indexOfFirstItem, indexOfLastItem);

  const getActivityIcon = (activityType) => {
    switch (activityType) {
      case "githubInteraction":
        return <SiGithub className="w-4 h-4 text-[#333333]" />;
      case "xInteraction":
        return <SiX className="w-4 h-4 text-[#1DA1F2]" />;
      case "AIActivity":
        return <Cpu className="w-4 h-4 text-[#15FF75]" />;
      default:
        return <Activity className="w-4 h-4 text-[#8AA5A2]" />;
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between mb-8">
        <h3 className="text-2xl font-bold text-[#E8FDF2]">Activity History</h3>
        <div className="flex items-center gap-2 text-sm text-[#8AA5A2]">
          <Activity className="w-4 h-4" />
          <span>{history.length} total activities</span>
        </div>
      </div>
      
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-white/10">
          <thead>
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-[#8AA5A2] uppercase tracking-wider">Date</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-[#8AA5A2] uppercase tracking-wider">Activity</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-[#8AA5A2] uppercase tracking-wider">Points</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-[#8AA5A2] uppercase tracking-wider">Details</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-[#8AA5A2] uppercase tracking-wider">Verify</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {currentHistory.map((entry, i) => (
              <motion.tr 
                key={i}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
                className="hover:bg-white/[0.02] transition-colors"
              >
                <td className="px-6 py-4 whitespace-nowrap text-sm text-[#E8FDF2]">
                  {formatDate(entry.timestamp)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center gap-2">
                    {getActivityIcon(entry.activityType)}
                    <span className="text-sm text-[#E8FDF2]">
                      {getActivityDisplayName(entry.activityType)}
                    </span>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-[#15FF75]/20 text-[#15FF75]">
                    +{entry.pointsEarned || 0}
                  </span>
                </td>
                <td className="px-6 py-4 text-sm text-[#8AA5A2] max-w-xs truncate">
                  {entry.details || 'No details available'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  {entry.txId ? (
                    <PremiumButton
                      variant="ghost"
                      disabled={false}
                      size="sm"
                      onClick={() => window.open(`https://ao.link/#/message/${entry.txId}`, '_blank')}
                      icon={<ExternalLink className="w-3 h-3" />}
                    >
                      Verify
                    </PremiumButton>
                  ) : (
                    <span className="text-[#5A6B68]">N/A</span>
                  )}
                </td>
              </motion.tr>
            ))}
            {currentHistory.length === 0 && (
              <tr>
                <td colSpan={5} className="px-6 py-8 text-center">
                  <div className="flex flex-col items-center gap-3">
                    <FileText className="w-12 h-12 text-[#8AA5A2]/50" />
                    <div className="text-[#8AA5A2]">No activity history available</div>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-6 pt-6 border-t border-white/10">
          <div className="text-sm text-[#8AA5A2]">
            Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, history.length)} of {history.length} activities
          </div>
          <div className="flex items-center gap-2">
            <PremiumButton
              variant="ghost"
              size="sm"
              onClick={() => onPageChange(Math.max(currentPage - 1, 1))}
              disabled={currentPage === 1}
              icon={<span className="rotate-180">→</span>}
            >
              Previous
            </PremiumButton>
            
            <div className="flex items-center gap-1">
              {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                const pageNum = currentPage <= 3 ? i + 1 : currentPage - 2 + i;
                if (pageNum > totalPages) return null;
                return (
                  <button
                    key={pageNum}
                    onClick={() => onPageChange(pageNum)}
                    className={`w-8 h-8 rounded-lg text-sm font-medium transition-all duration-300 ${
                      currentPage === pageNum
                        ? 'bg-[#15FF75] text-[#0A0E1B]'
                        : 'text-[#8AA5A2] hover:text-[#E8FDF2] hover:bg-white/[0.05]'
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>
            
            <PremiumButton
              variant="ghost"
              size="sm"
              onClick={() => onPageChange(Math.min(currentPage + 1, totalPages))}
              disabled={currentPage === totalPages}
              icon={<span>→</span>}
            >
              Next
            </PremiumButton>
          </div>
        </div>
      )}
    </div>
  );
};

export default ActivityHistory;